import React from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ArrowRight,
  Heart,
  Cake,
  Briefcase,
  Sparkles,
  Film,
  Music,
  Scissors,
  Cloud,
  UsbIcon,
  Award,
} from 'lucide-react';
import Reveal from '../components/Reveal';
import PersonalisedSongPromo from '../components/PersonalisedSongPromo';

/**
 * ServicesPage - simplified, premium, conversion-focused rewrite.
 *
 * Key changes vs old page:
 *  - Reduced occasion chips (6 broad categories instead of 15+)
 *  - Merged 4 individual service cards → 2 grouped narrative sections
 *  - Removed per-card "What's Included" lists (moved to one shared block)
 *  - Alternating image/text layout for scannability
 *  - Emotion-first copy (no "we create/craft/produce" repetition)
 *  - Strong closing CTA
 */

const OCCASIONS = [
  'Weddings',
  'Memorials',
  'Birthdays',
  'Anniversaries',
  'Corporate',
  'Special Occasions',
];

const SERVICE_SECTIONS = [
  {
    id: 'weddings',
    kicker: 'Wedding Cinematic Films',
    title: 'Every moment. Beautifully remembered.',
    body:
      "Relive every smile, laugh and unforgettable moment from your wedding day in a beautifully crafted cinematic presentation you'll treasure forever.",
    image:
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&h=800&fit=crop',
    icon: Sparkles,
    reverse: false,
  },
  {
    id: 'memorials',
    kicker: 'Memorial Tributes',
    title: 'A dignified tribute to a beautiful life.',
    body:
      "A heartfelt cinematic tribute crafted with warmth, care and quiet elegance - honouring the people we love in the way they deserve.",
    image:
      'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=1200&h=800&fit=crop',
    icon: Heart,
    reverse: true,
  },
  {
    id: 'celebrations',
    kicker: 'Celebrations',
    title: 'Milestones worth marking.',
    body:
      "Birthdays, anniversaries, engagements, retirements, baby showers, christenings and family gatherings - transformed into vibrant cinematic memories that capture the joy of the day.",
    image:
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200&h=800&fit=crop',
    icon: Cake,
    reverse: false,
  },
  {
    id: 'business',
    kicker: 'Business & Events',
    title: 'Stories that move audiences.',
    body:
      "Corporate films, product launches, fundraisers, school reunions and brand storytelling - cinematic content designed to inspire, engage and leave a lasting impression.",
    image:
      'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=1200&h=800&fit=crop',
    icon: Briefcase,
    reverse: true,
  },
];

const INCLUDES = [
  { icon: Scissors, text: 'Professional editing' },
  { icon: Music, text: 'Custom music synchronisation' },
  { icon: Film, text: 'Smooth cinematic transitions' },
  { icon: Sparkles, text: 'Photos & videos combined' },
  { icon: Cloud, text: 'Secure private Google Drive delivery' },
  { icon: UsbIcon, text: 'Premium USB drive included' },
  { icon: Award, text: '4K Ultra HD export' },
];

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-black pt-24 md:pt-40 pb-24" data-testid="services-page">
      {/* ═══════════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════════ */}
      <div className="container mx-auto px-4 lg:px-8">
        <Reveal className="text-center max-w-3xl mx-auto mb-10">
          <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Our Services</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Cinematic films for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              every occasion
            </span>
          </h1>
        </Reveal>

        {/* Occasion chips - 6 broad categories only */}
        <Reveal delay={1} className="mb-8">
          <div className="flex flex-wrap items-center justify-center gap-3 max-w-3xl mx-auto">
            {OCCASIONS.map((tag) => (
              <span
                key={tag}
                className="px-4 py-2 text-sm rounded-full border border-white/10 bg-white/[0.03] text-gray-300 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6 max-w-xl mx-auto italic">
            Don't see your occasion? No problem. Every cinematic presentation is professionally created to tell your unique story.
          </p>
        </Reveal>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          ALTERNATING SERVICE SECTIONS
          ═══════════════════════════════════════════════════════════ */}
      <div className="container mx-auto px-4 lg:px-8 mt-20 space-y-24">
        {SERVICE_SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <Reveal
              key={s.id}
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto ${
                s.reverse ? '' : ''
              }`}
              data-testid={`service-${s.id}`}
            >
              {/* Image */}
              <div className={`${s.reverse ? 'lg:order-2' : ''}`}>
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl group">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-black/70 backdrop-blur-md border border-amber-500/50 flex items-center justify-center">
                    <Icon size={20} className="text-amber-400" strokeWidth={1.5} />
                  </div>
                </div>
              </div>

              {/* Copy */}
              <div className={`space-y-6 ${s.reverse ? 'lg:order-1' : ''}`}>
                <p className="uppercase tracking-[0.3em] text-xs text-amber-400">{s.kicker}</p>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight font-editorial">
                  {s.title}
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">{s.body}</p>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium transition-colors group"
                  data-testid={`service-${s.id}-cta`}
                >
                  Start Your Project
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          EVERY PACKAGE INCLUDES
          ═══════════════════════════════════════════════════════════ */}
      <div className="container mx-auto px-4 lg:px-8 mt-28">
        <Reveal className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Every Package Includes</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Premium as <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">standard</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {INCLUDES.map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-4 p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-amber-500/40 hover:bg-white/[0.04] transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-full border border-amber-500/40 bg-black flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-amber-400" strokeWidth={1.75} />
                </div>
                <span className="text-white text-[15px]">{text}</span>
              </div>
            ))}
          </div>

          {/* Privacy reassurance */}
          <p className="text-center text-sm text-gray-500 mt-10 max-w-2xl mx-auto italic">
            Your presentation is delivered in its original high quality via a secure private Google Drive link. Only you and anyone you choose to share the link with will have access, keeping your memories private and secure.
          </p>

          {/* Private & Secure Delivery info box */}
          <div
            className="mt-12 max-w-3xl mx-auto rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.04] to-transparent p-7 md:p-9"
            data-testid="private-delivery-box"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full border border-amber-500/40 bg-black flex items-center justify-center flex-shrink-0">
                <Cloud size={20} className="text-amber-400" strokeWidth={1.5} />
              </div>
              <div className="space-y-3">
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  Private &amp; <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Secure Delivery</span>
                </h3>
                <p className="text-gray-300 text-[15px] leading-relaxed">
                  Your completed presentation will be delivered via a secure private Google Drive download link.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  To help keep your memories secure and our cloud storage running efficiently, your download link will remain available for <span className="text-amber-300 font-medium">30 days</span> from the date of delivery.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We recommend downloading and saving your presentation to your computer, phone, external hard drive or cloud storage as soon as you receive it.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  <span className="text-amber-300 font-medium">Every Visual Creationz order includes a physical USB copy</span> of your finished presentation, giving you both a convenient digital version and a physical copy to keep.
                </p>
                <p className="text-gray-500 text-xs leading-relaxed pt-2">
                  Production times refer to the creation of your digital film. Physical USB delivery times may vary depending on postage.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Personalised song upgrade */}
      <div className="mt-16">
        <PersonalisedSongPromo testId="services-song-promo" />
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CLOSING CTA
          ═══════════════════════════════════════════════════════════ */}
      <div className="container mx-auto px-4 lg:px-8 mt-28">
        <Reveal className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-10 md:p-16 text-center max-w-5xl mx-auto">
          <div
            className="absolute inset-0 opacity-60 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at 50% 50%, rgba(201, 162, 39, 0.15), transparent 60%)',
            }}
          />
          <div className="relative z-10 max-w-3xl mx-auto space-y-6">
            <p className="uppercase tracking-[0.3em] text-xs text-amber-400">Every Story Deserves</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight font-editorial">
              Every story deserves to be{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                remembered
              </span>
              .
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              Whether it's a wedding, birthday, memorial, anniversary or business event, Visual Creationz transforms your memories into beautifully crafted cinematic presentations you'll be proud to watch and share.
            </p>

            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto pt-2">
              {[
                'Secure private Google Drive delivery',
                'Physical USB copy included',
                '4K Ultra HD quality',
                'Professionally edited & personalised',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2 text-left">
                  <Check size={16} className="text-amber-400 mt-1 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-sm text-gray-300">{item}</span>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Link
                to="/pricing"
                data-testid="services-final-cta"
                className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-2xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95"
              >
                Start Your Project
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default ServicesPage;
